import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../api';

import { FILE_BASE_URL } from '../constants';

const resolveImageSrc = (value) => {
  if (!value) return '';
  if (value.startsWith('http')) return value;
  return `${FILE_BASE_URL}${value}`;
};

const TrainingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreateMode = !id || id === 'create';

  const [formData, setFormData] = useState({
    title: '',
    image: '',
    description: '',
    content: '',
    status: 'Active'
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);

  const previewSrc = imagePreview || resolveImageSrc(formData.image);

  useEffect(() => {
    if (!isCreateMode && id) {
      const fetchTraining = async () => {
        try {
          const response = await api.get(`/trainings/${id}`);
          setFormData(response.data);
          setImagePreview(resolveImageSrc(response.data.image));
        } catch (error) {
          console.error("Error fetching training:", error);
        }
      };
      fetchTraining();
    } else {
      setFormData({
        title: '',
        image: '',
        description: '',
        content: '',
        status: 'Active'
      });
      setImageFile(null);
      setImagePreview('');
    }
  }, [id, isCreateMode]);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content: content
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    setImagePreview(prev => {
      if (prev && prev.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      if (file) {
        return URL.createObjectURL(file);
      }
      return resolveImageSrc(formData.image);
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = { ...formData };
      const multipart = new FormData();
      multipart.append('training', JSON.stringify(payload));
      if (imageFile instanceof File) {
        multipart.append('image', imageFile);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      if (isCreateMode) {
        await api.post('/trainings', multipart, config);
      } else {
        await api.put(`/trainings/${id}`, multipart, config);
      }
      navigate('/training');
    } catch (error) {
      console.error("Error saving training:", error);
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  return (
    <div className="job-detail">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/training')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="page-title">{isCreateMode ? 'Thêm bài viết đào tạo mới' : 'Chỉnh sửa bài viết đào tạo'}</h1>
        </div>
        <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
          <Save size={18} /> {loading ? 'Đang lưu...' : 'Lưu lại'}
        </button>
      </div>

      <div className="form-section">
        <h3 className="section-title">Thông tin chung</h3>
        <div className="form-group">
          <label>Tiêu đề bài viết</label>
          <input
            type="text"
            className="form-input"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Nhập tiêu đề"
          />
        </div>

        <div className="form-group">
          <label>Ảnh bìa</label>
          <input
            type="file"
            className="form-input"
            accept="image/*"
            onChange={handleFileChange}
          />
          {previewSrc && (
            <div style={{ marginTop: '8px' }}>
              <img src={previewSrc} alt="Preview" style={{ height: '100px', borderRadius: '4px' }} />
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Mô tả ngắn</label>
          <textarea
            className="form-input"
            name="description"
            style={{ minHeight: '80px', resize: 'vertical' }}
            value={formData.description}
            onChange={handleChange}
            placeholder="Nhập mô tả ngắn"
          />
        </div>

        <div className="form-group">
          <label>Trạng thái</label>
          <select
            className="form-select"
            name="status"
            value={formData.status}
            onChange={handleChange}
          >
            <option value="Active">Hoạt động</option>
            <option value="Inactive">Ẩn</option>
          </select>
        </div>
      </div>

      <div className="form-section">
        <h3 className="section-title">Nội dung chi tiết</h3>
        <div className="editor-placeholder">
          <ReactQuill
            theme="snow"
            value={formData.content}
            onChange={handleContentChange}
            modules={modules}
            formats={formats}
            className="editor-content"
          />
        </div>
      </div>

      <style>{`
        .job-detail {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .form-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #E0E0E0;
        }
        .section-title {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 16px;
          color: #1A1A1A;
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #1A1A1A;
        }
        .form-input, .form-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #E0E0E0;
          border-radius: 6px;
          font-size: 14px;
          background: #F5F7FA;
          box-sizing: border-box;
        }
        .form-grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .date-input-wrapper {
          position: relative;
        }
        .input-icon-right {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
        }
        .editor-placeholder {
          border: 1px solid #E0E0E0;
          border-radius: 6px;
          overflow: hidden;
          background: white;
        }
        .editor-content .ql-container {
            min-height: 300px;
            font-size: 14px;
        }
        .editor-content .ql-editor {
            min-height: 300px;
        }
      `}</style>
    </div>
  );
};

export default TrainingDetail;
